// A generic default-deny confirm dialog for Validate's Flow 1 ("Build anyway"/"Drop",
// docs/phase-0/slice5-ux-flows.md §4.5) - reuses the exact focus-trap SHELL mechanics
// `GdprRoutingPanel.tsx` already ships (`.gdpr-routing-panel > .card`, heading focused on
// open, Tab/Shift+Tab cycle within, Escape = cancel, focus returns to the trigger on
// close - docs/phase-0/slice5-component-designs.md §2.6), reimplemented here (not
// imported) because `GdprRoutingPanel` renders GDPR-routing-item content specifically and
// this lane does not own that file. Real escape route always works (`escape-routes`).
import { useEffect, useId, useRef } from "react";

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const ValidateConfirmDialog = ({
  heading,
  body,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  confirming = false,
  error = null,
  triggerRef,
}: {
  readonly heading: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly confirming?: boolean;
  /** An inline retry error from a prior failed attempt - rendered INSIDE the dialog
   * (this panel is `position: fixed` over the whole page, so an error rendered by the
   * caller outside it would be invisible). */
  readonly error?: string | null;
  readonly triggerRef?: React.RefObject<HTMLElement | null>;
}): React.JSX.Element => {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();

  useEffect(() => {
    headingRef.current?.focus();
    return () => {
      triggerRef?.current?.focus();
    };
  }, [triggerRef]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key !== "Tab" || panelRef.current === null) return;
    const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (first === undefined || last === undefined) return;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="gdpr-routing-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      ref={panelRef}
      onKeyDown={handleKeyDown}
      data-testid="validate-confirm-dialog"
    >
      <div className="card pad">
        <h2 className="h3" id={headingId} tabIndex={-1} ref={headingRef}>
          {heading}
        </h2>
        <p className="muted mt8" style={{ fontSize: 13.5 }}>
          {body}
        </p>
        {error !== null ? (
          <p className="field-error mt8" role="alert">
            {error}
          </p>
        ) : null}
        <div className="row gap12 mt16 wrap-g">
          <button
            type="button"
            className="btn btn-primary"
            disabled={confirming}
            onClick={onConfirm}
          >
            {confirming ? "..." : confirmLabel}
          </button>
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
